import { useFleetAuth } from "../auth/fleetAuth";
import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { formatVehicleName } from "../utils/tractorNameRemapping";

// One localization source (SW-5493): last-known position plus accuracy.
// lat/lon are null until the source has ever reported a real fix;
// horizontalStdM is the 1-sigma horizontal error in meters from the latest
// heartbeat (null while the source is unusable).
export type LocationSource = {
  lat: number | null;
  lon: number | null;
  horizontalStdM: number | null;
};

export type Tractor = {
  displayName: string;
  statuses: {
    online: boolean;
    autonomy_enabled: boolean;
    destination: string | null;
    requestedRide: string | null;
    // Epoch ms of the last message received from the vehicle. Only present
    // for AeroVect users — the backend strips it for customer tiers.
    lastSeenAt: number | null;
    // Dual-source localization (SW-5493): the raw GPS fix and the fused EKF
    // estimate, same shape, plus the backend's advisory pick. null for
    // roster-injected vehicles that carry no statuses at all.
    location: {
      recommendedSource: "gps" | "kinematic" | null;
      gps: LocationSource;
      kinematic: LocationSource;
    } | null;
  };
  metrics: {
    [key: string]: any;
  };
};

// The position to render for a tractor: the recommended source's last-known
// fix, falling back to GPS then kinematic, or null when no source has ever
// reported one.
export function displayPosition(
  tractor: Tractor,
): { lat: number; lon: number } | null {
  const location = tractor.statuses.location;
  if (!location) return null;
  const preferred =
    location.recommendedSource === "kinematic"
      ? [location.kinematic, location.gps]
      : [location.gps, location.kinematic];
  for (const source of preferred) {
    if (source.lat !== null && source.lon !== null) {
      return { lat: source.lat, lon: source.lon };
    }
  }
  return null;
}

// Localization 1-sigma horizontal error (meters) for the recommended source,
// or null if this tractor reports no confidence data. Drives the map's
// confidence range; falls back to GPS then kinematic when no source is
// recommended.
export function localizationStdM(tractor: Tractor): number | null {
  const location = tractor.statuses.location;
  if (!location) return null;
  const recommended =
    location.recommendedSource === "kinematic"
      ? location.kinematic.horizontalStdM
      : location.recommendedSource === "gps"
        ? location.gps.horizontalStdM
        : null;
  return (
    recommended ??
    location.gps.horizontalStdM ??
    location.kinematic.horizontalStdM
  );
}

interface TractorContextType {
  tractors: Map<string, Tractor>;
  setTractors: React.Dispatch<React.SetStateAction<Map<string, Tractor>>>;
}

interface TractorProviderProps {
  children: React.ReactNode;
}

const TractorContext = createContext<TractorContextType | undefined>(undefined);

export const TractorProvider: React.FC<TractorProviderProps> = ({
  children,
}) => {
  const [tractors, setTractors] = useState<Map<string, Tractor>>(new Map());
  const { isAuthenticated, getAccessToken } = useFleetAuth();

  // Ensure backend server url is set. An explicit empty string is the
  // dev-server-proxy mode used by start-beta / start-prod (see setupProxy.js),
  // which produces same-origin relative URLs like "/tractors" — that's
  // intentional and not an error.
  if (process.env.REACT_APP_BACKEND_SERVER_URL === undefined) {
    console.error("BACKEND URL NOT SET!");
  }

  /**
   * Converts destination names from uppercase with spaces to Title Case.
   * This function uses a lookup to convert specific known names, falling back to a generic conversion if the name is not found.
   *
   * @param destination - The original destination name in uppercase.
   * @returns The converted destination name in Title Case.
   */
  const convertDestinationName = (
    destination: string | null,
  ): string | null => {
    const lookup: { [key: string]: string } = {
      "CARGO YARD": "Cargo Yard",
      "T-POINT": "T-Point",
    };

    if (destination && lookup[destination]) {
      return lookup[destination];
    }

    // Fallback for any other destinations not explicitly listed in the lookup
    return destination
      ? destination
          .split(" ")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ")
      : null;
  };

  /**
   * Extracts tractors from API response or dummy data and converts them into a Map.
   * This function handles the conversion of property names from snake_case to camelCase
   * and ensures that all tractors have the necessary default statuses and metrics.
   *
   * @param data - The raw data object received from the API or dummy data.
   * @returns A Map of tractors keyed by their identifiers.
   */
  const extractTractorsFromRawData = useCallback(
    (data: { [key: string]: any }): Map<string, Tractor> => {
      const tractorsMap = new Map<string, Tractor>();
      for (const [key, value] of Object.entries(data)) {
        // If the tractor has no statuses, default it to offline and null
        if (!value?.statuses) {
          value["statuses"] = {
            online: false,
            autonomy_enabled: false,
            destination: null,
            requestedRide: null,
            location: null,
            last_seen_at: null,
          };
        }

        // Convert destination name using the helper function
        const convertedDestination = convertDestinationName(
          value?.statuses.destination,
        );

        // Map the dual-source location block (snake_case -> camelCase),
        // defaulting to null for roster-injected vehicles with no statuses.
        const mapSource = (raw: any): LocationSource => ({
          lat: raw?.lat ?? null,
          lon: raw?.lon ?? null,
          horizontalStdM: raw?.horizontal_std_m ?? null,
        });
        const rawLocation = value?.statuses?.location;
        const location = rawLocation
          ? {
              recommendedSource: rawLocation.recommended_source ?? null,
              gps: mapSource(rawLocation.gps),
              kinematic: mapSource(rawLocation.kinematic),
            }
          : null;

        // Put the JSON object into a Tractor object
        let tractor: Tractor = {
          displayName: formatVehicleName(key),
          statuses: {
            online: value?.statuses.online,
            autonomy_enabled: value?.statuses.autonomy_enabled ?? null,
            destination: convertedDestination,
            requestedRide: value?.statuses.requested_ride ?? null,
            lastSeenAt: value?.statuses.last_seen_at ?? null,
            location,
          },
          metrics: value.metrics ?? {},
        };
        // Put the tractor in the tractors map
        tractorsMap.set(key, tractor);
      }
      return tractorsMap;
    },
    [],
  );

  /**
   * Fetches tractor data from the API.
   * Includes an authorization token for authenticated requests.
   *
   * @param authToken - Bearer token for authenticated requests.
   * @returns The raw data from the API.
   */
  const fetchTractorData = async (
    authToken: string | null = null,
  ): Promise<any> => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/tractors`,
        { headers },
      );

      // Validate response structure
      if (response.data && response.data.data) {
        return response.data.data;
      }
      console.warn("Unexpected response structure:", response.data);
      return null;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with error status
          console.error(
            `Backend error (${error.response.status}):`,
            error.response.statusText,
          );
        } else if (error.request) {
          // Request made but no response (backend likely down)
          console.error(
            "Backend is not responding. Is the server running at",
            process.env.REACT_APP_BACKEND_SERVER_URL,
            "?",
          );
        } else {
          console.error("Request setup error:", error.message);
        }
      } else {
        console.error("Failed to fetch tractor data:", error);
      }
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getAccessToken();
        const rawData = await fetchTractorData(token);

        // Only process data if we got valid data back
        if (rawData) {
          const tractorsMap = extractTractorsFromRawData(rawData);
          setTractors(tractorsMap);
        } else {
          // If no data, set empty map (vehicles will show as offline)
          console.warn("No tractor data received, setting empty map");
          setTractors(new Map());
        }
      } catch (error) {
        console.error(`Error fetching or processing tractor data: ${error}`);
        // Set empty map on error to prevent crashes
        setTractors(new Map());
      }
    };

    // Fetch tractor data once the user authenticates, then every 10 seconds after that
    if (isAuthenticated) {
      fetchData();

      const interval = setInterval(fetchData, 10000);

      // Stop fetching once the app is closed
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, extractTractorsFromRawData, getAccessToken]);

  return (
    <TractorContext.Provider value={{ tractors, setTractors }}>
      {children}
    </TractorContext.Provider>
  );
};

export const useTractors = () => {
  const context = useContext(TractorContext);
  if (context === undefined) {
    throw new Error("useTractors must be used within a TractorProvider");
  }
  return context;
};

/**
 * The live fleet if there is one, or an empty one if this tree has no provider.
 *
 * For a consumer that *decorates* rather than depends — Tractor State's
 * location card, which is supplementary context beside a record sourced from a
 * different API entirely. The provider is app-wide today, so this returns real
 * data in the app; the point is that such a consumer degrades to "no position
 * to show" instead of taking the page down with it, and can be rendered in
 * isolation without standing up Auth0 to satisfy a hook it only reads from.
 */
export const useTractorsIfAvailable = (): Map<string, Tractor> =>
  useContext(TractorContext)?.tractors ?? EMPTY_FLEET;

const EMPTY_FLEET: Map<string, Tractor> = new Map();
